import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { API_URL } from '../lib/config';
import './UserDetailPage.css';
import './AdminPage.css'; // [추가] 테이블/목록 스타일 재사용을 위해 임포트

function UserDetailPage() {
    const { userId } = useParams();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { currentUser } = useAuth();

    useEffect(() => {
        // [수정] currentUser가 확인된 후에만 데이터를 불러옵니다.
        if (!currentUser) return;

        const fetchUserDetails = async () => {
            try {
                // [수정] ID 토큰을 가져옵니다.
                const idToken = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/api/admin/users/${userId}`,{
                    // [수정] headers에 인증 토큰을 추가합니다.
                    headers: {
                        'Authorization': `Bearer ${idToken}`
                    }
                });
                const data = await response.json();
                if (response.ok) {
                    setUserData(data);
                }
            } catch (error) {
                console.error("사용자 상세 정보 로딩 오류:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchUserDetails();
    }, [userId, currentUser]);
    
    const handleSuspendUser = async (durationDays, durationText) => {
        setIsDropdownOpen(false);
        if (window.confirm(`${userData.user.userName} 사용자에게 '${durationText}' 조치를 적용하시겠습니까?`)) {
            try {
                // [수정] 관리자 본인의 ID 토큰을 가져옵니다.
                const idToken = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/api/admin/users/${userId}/suspend`, {
                    method: 'PUT',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${idToken}` // [수정] 헤더에 토큰 추가
                    },
                    body: JSON.stringify({ durationDays })
                });

                const data = await response.json();
                if (!response.ok) throw new Error(data.message);
                
                alert(data.message);
                setUserData(prev => ({ ...prev, user: data.user }));

            } catch (error) {
                alert(`오류가 발생했습니다: ${error.message}`);
            }
        }
    };
    
    const handleUnsuspendUser = async () => {
        if (window.confirm(`${userData.user.userName} 사용자의 정지를 해제하시겠습니까?`)) {
            try {
                // TODO: 나중에 관리자 인증을 위해 ID 토큰을 추가해야 합니다.
                // [수정] 관리자 본인의 ID 토큰을 가져옵니다.
                const idToken = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/api/admin/users/${userId}/unsuspend`, {
                    method: 'PUT',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${idToken}` // [수정] 헤더에 토큰 추가
                    },
                });

                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.message || '정지 해제에 실패했습니다.');
                }

                alert(data.message);
                
                // 화면에 변경된 사용자 상태를 즉시 반영합니다.
                setUserData(prev => ({ ...prev, user: data.user }));

            } catch (error) {
                console.error("사용자 정지 해제 실패:", error);
                alert(`오류가 발생했습니다: ${error.message}`);
            }
        }
    };

    if (loading) return <div className="user-detail-container">로딩 중...</div>;
    if (!userData) return <div className="user-detail-container">사용자 정보를 찾을 수 없습니다.</div>;

    const { user, reportsMade, reportsReceived, chatRooms } = userData;

    return (
        <div className="user-detail-container">
            <h1>
                {user?.userName}
                <span>({user?.email})</span>
            </h1>

            {user?.status === 'suspended' && user.suspendedUntil && (
                <div className="suspension-banner">
                    <strong>이 사용자는 현재 정지 상태입니다.</strong>
                    <p>정지 만료일: {new Date(user.suspendedUntil).toLocaleString('ko-KR')}</p>
                </div>
            )}
             {user?.status === 'banned' && (
                 <div className="suspension-banner">
                    <strong>이 사용자는 영구 정지 상태입니다.</strong>
                </div>
            )}

            <div className="user-controls">
                {(user?.status || 'active') === 'active' ? (
                    <div className="dropdown">
                        <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="suspend-button">
                            제재 조치 ▾
                        </button>
                        {isDropdownOpen && (
                            <div className="dropdown-menu">
                                <button className="dropdown-item" onClick={() => handleSuspendUser(1, '1일 이용 정지')}>1일 이용 정지</button>
                                <button className="dropdown-item" onClick={() => handleSuspendUser(7, '7일 이용 정지')}>7일 이용 정지</button>
                                <button className="dropdown-item" onClick={() => handleSuspendUser(30, '30일 이용 정지')}>30일 이용 정지</button>
                                <button className="dropdown-item" onClick={() => handleSuspendUser(99999, '영구 이용 정지')}>영구 이용 정지</button>
                            </div>
                        )}
                    </div>
                ) : (
                    <button onClick={handleUnsuspendUser} className="unsuspend-button">
                        계정 정지 해제
                    </button>
                )}
            </div>

            <div className="detail-section">
                <h2>기본 정보</h2>
                <div className="detail-grid">
                    <div><strong>가입일:</strong> {new Date(user?.createdAt).toLocaleString()}</div>
                    <div><strong>상태:</strong> {user?.status || 'active'}</div>
                </div>
            </div>

            <div className="detail-section">
                <h2>AI 프로필 목록 ({user?.profiles?.length || 0}개)</h2>
                <ul className="detail-list">
                    {user?.profiles?.map(profile => (
                        <li key={profile._id} className="detail-list-item">
                            <strong>{profile.nickname}</strong> {profile.isRepresentative && '(대표)'} <br/>
                            <small>{profile.character}</small>
                        </li>
                    ))}
                </ul>
            </div>
            
            {/* [수정] 신고한 내역 목록 표시 */}
            <div className="detail-section">
                <h2>신고한 내역 ({reportsMade?.length || 0}건)</h2>
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>신고일</th>
                            <th>피신고자</th>
                            <th>상태</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportsMade?.map(report => (
                            <tr key={report._id}>
                                <td>
                                    {/* [수정] report.chatRoom을 사용하여 올바른 경로로 링크 */}
                                    <Link to={`/admin/reports/chatroom/${report.chatRoom}`}>{new Date(report.createdAt).toLocaleString()}</Link>
                                </td>
                                <td>{report.reportedUser?.userName}</td>
                                <td>{report.status}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* [수정] 신고받은 내역 목록 표시 */}
            <div className="detail-section">
                <h2>신고받은 내역 ({reportsReceived?.length || 0}건)</h2>
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>신고일</th>
                            <th>신고자</th>
                            <th>상태</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportsReceived?.map(report => (
                            <tr key={report._id}>
                                <td>
                                    {/* [수정] report.chatRoom을 사용하여 올바른 경로로 링크 */}
                                    <Link to={`/admin/reports/chatroom/${report.chatRoom}`}>{new Date(report.createdAt).toLocaleString()}</Link>
                                </td>
                                <td>{report.reportingUser?.userName}</td>
                                <td>{report.status}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {/* [수정] 참여한 채팅방 목록 표시 */}
            <div className="detail-section">
                <h2>참여한 채팅방 목록 ({chatRooms?.length || 0}개)</h2>
                <table className="admin-table">
                     <thead>
                        <tr>
                            <th>생성일</th>
                            <th>상태</th>
                        </tr>
                    </thead>
                    <tbody>
                        {chatRooms?.map(room => (
                            <tr key={room._id}>
                                <td>{new Date(room.createdAt).toLocaleString()}</td>
                                <td>{room.isActive ? '진행중' : '종료'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div> 
        </div>
    );
}

export default UserDetailPage;